/* eslint-disable react-hooks/rules-of-hooks */

import classNames from 'classnames'
import React, { useMemo } from 'react'
import type { FC } from 'react'

import { useListContext } from './list-context'
import type { ListItemTextProps } from './types'
import type { Theme } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import extractStringNodes from '../../utils/extract-string-nodes'
import { get } from '../../utils/object'
import { getNextSize, resolveSize } from '../../utils/size-utils'
import { FlexPrivate } from '../_primitives/flex/flex'
import { TextPrivate } from '../_primitives/text/text'

const ListItemText: FC<ListItemTextProps> = React.forwardRef<
  HTMLDivElement,
  ListItemTextProps & { theme?: Theme }
>(function ListItemText(props, ref) {
  const {
    className,
    children,
    disableTextWrap,
    inset = false,
    primary: primaryNode,
    primaryTextProps,
    secondary: secondaryNode,
    secondaryTextProps,
    size,
    disabled,
    ...rest
  } = props
  const { dense } = useListContext()
  const theme = useSafeThemeUI(props)
  const fontSize = resolveSize(size, get(theme, 'sizes.font'))
  const secondaryFontSize = resolveSize(
    Array.isArray(size)
      ? size.map(i => getNextSize(i, -1))
      : getNextSize(size, -1),
    get(theme, 'sizes.font')
  )
  let primaryChildren = primaryNode != null ? primaryNode : children

  if (!disableTextWrap) {
    const primaryStringNode = extractStringNodes(
      React.Children.toArray(primaryChildren).filter(i => typeof i === 'string')
    )
    if (primaryStringNode != null && primaryStringNode.length > 0) {
      primaryChildren = (
        <TextPrivate
          className="csg-tui-list-item-text-primary"
          ellipsis
          {...{
            fontSize: fontSize,
            ...primaryTextProps
          }}
        >
          {primaryChildren}
        </TextPrivate>
      )
    }
  }

  let secondaryChildren = secondaryNode != null ? secondaryNode : []

  if (!disableTextWrap) {
    const secondaryStringNode = extractStringNodes(
      React.Children.toArray(secondaryChildren).filter(
        i => typeof i === 'string'
      )
    )
    if (secondaryStringNode != null && secondaryStringNode.length > 0) {
      secondaryChildren = (
        <TextPrivate
          className="csg-tui-list-item-text-secondary"
          ellipsis
          {...{
            fontSize: secondaryFontSize,
            color: 'neutral.dark',
            ...secondaryTextProps
          }}
        >
          {secondaryChildren}
        </TextPrivate>
      )
    }
  }
  const __css = useMemo(
    () => ({
      flexDirection: 'column',
      flex: '1 1 auto',
      gap: 'xs-3',
      minWidth: 0,
      ...(inset ? { pl: '56px' } : {}),
      ...(primaryChildren && secondaryNode
        ? { my: dense ? 'xs-2' : 'xxs' }
        : { my: dense ? 'xs-3' : 'xs-2' }),
      '&.is-disabled': {
        pointerEvents: 'none'
      }
    }),
    [dense, inset, primaryChildren, secondaryNode]
  )
  // eslint-disable-next-line no-console
  // console.log(`---- render theme-ui.list-item-text ----`)
  return (
    <FlexPrivate
      ref={ref}
      className={classNames('csg-tui-list-item-text', className, {
        'is-disabled': disabled
      })}
      __css={__css}
      {...rest}
    >
      {primaryChildren}
      {secondaryChildren}
    </FlexPrivate>
  )
})

export default ListItemText
